'use client';

import { useCallback, useState } from 'react';

import { useRouter } from 'next/navigation';

import {
  SELF_DISCLOSURE_PRESETS,
  type SelfDisclosureConfig,
  type SelfDisclosureConfigAuthor,
  renderSelfDisclosurePdf,
} from '@kit/self-disclosure';
import { GeneratorForm } from '@kit/self-disclosure/client';
import { useAppEvents } from '@kit/shared/events';
import { useUser } from '@kit/supabase/hooks/use-user';

import {
  loadSaveTargetsAction,
  parkToolTemplateAction,
  saveToolTemplateToWorkspacesAction,
} from '~/lib/tools/selbstauskunft/actions';
import type { WorkspaceSaveTarget } from '~/lib/tools/selbstauskunft/tool-template.schema';
import { WorkspaceSaveDialog } from '~/lib/tools/selbstauskunft/workspace-save-dialog';

import { authHref } from '../../_lib/marketing-routes';

/**
 * Marketing landing-page generator island (Sanierungs-Spec §2.6). Wraps the
 * shared `GeneratorForm` (@kit/self-disclosure/client) and supplies the
 * terminal actions via `renderFooter`:
 *
 *  - CTA A „PDF kostenlos herunterladen": renders the PDF fully IN THE BROWSER
 *    (pdf-lib is isomorphic) and downloads it via a Blob URL – no server
 *    contact, no persistence. The rendered PDF is white-label (no Locari/Ametis
 *    branding). The LP promise „ohne Save wird nichts gespeichert" stays true.
 *  - CTA B „Vorlage in Locari speichern": logged-in users get the multi-select
 *    workspace picker (`WorkspaceSaveDialog`); anonymous users get the
 *    park-&-claim flow (config parked server-side, sign-up with `next`
 *    pointing at the claim page).
 *
 * Analytics (Sanierungs-Spec §6.4): four events flow through the app event bus
 * and are mapped to PostHog in `analytics-provider.tsx`. Payloads NEVER carry
 * form contents / field keys / sender data – only config metadata (preset,
 * person_count, language, field_count; plus `logged_in` on the save click).
 */

interface IslandState {
  busy: null | 'download' | 'save';
  saveError: string | null;
  picker:
    | { open: false }
    | {
        open: true;
        // Author config (mandatory address) – this is the value handed to the
        // Author-typed save action (Spec 2026-07-08 §3.2).
        config: SelfDisclosureConfigAuthor;
        targets: WorkspaceSaveTarget[];
      };
}

const INITIAL_STATE: IslandState = {
  busy: null,
  saveError: null,
  picker: { open: false },
};

/**
 * Preset detection for analytics (§6.4): a config counts as a preset when its
 * field selection EQUALS the preset's field set – presets are UI shortcuts,
 * the field set is the only truth (Spec §2.4), so equality is derivable.
 */
function detectPreset(
  config: SelfDisclosureConfig,
): 'basis' | 'nach_besichtigung' | 'vor_vertragsabschluss' | 'custom' {
  const enabled = new Set(config.enabledFields);

  for (const preset of SELF_DISCLOSURE_PRESETS) {
    if (
      preset.fieldKeys.length === enabled.size &&
      preset.fieldKeys.every((key) => enabled.has(key))
    ) {
      return preset.key;
    }
  }

  return 'custom';
}

/** Data-minimal event payload (§6.4) – config metadata only. */
function configEventPayload(config: SelfDisclosureConfig) {
  return {
    preset: detectPreset(config),
    person_count: config.personCount,
    language: config.language,
    field_count: config.enabledFields.length,
  };
}

/** Download file name (§6.2): no phase axis anymore. */
function downloadFileName(config: SelfDisclosureConfig): string {
  return config.personCount > 1
    ? `Mieterselbstauskunft-${config.personCount}-Personen.pdf`
    : 'Mieterselbstauskunft.pdf';
}

export function SelbstauskunftGenerator() {
  const router = useRouter();
  const appEvents = useAppEvents();
  const { data: user } = useUser();

  const [state, setState] = useState<IslandState>(INITIAL_STATE);

  // `toolOpened` fires once per pageview on the first REAL user interaction –
  // the shared form's explicit `onDirty` contract (fires exactly once, never
  // on mount) replaces the former mountSeen heuristic (Spec §2.6/M9).
  const handleDirty = useCallback(() => {
    appEvents.emit({ type: 'selbstauskunft.toolOpened', payload: {} });
  }, [appEvents]);

  const renderBytes = useCallback(
    async (config: SelfDisclosureConfig) => {
      const bytes = await renderSelfDisclosurePdf(config, {
        now: new Date(),
      });

      appEvents.emit({
        type: 'selbstauskunft.generated',
        payload: configEventPayload(config),
      });

      return bytes;
    },
    [appEvents],
  );

  const handleDownload = useCallback(
    async (config: SelfDisclosureConfig) => {
      setState((s) => ({ ...s, busy: 'download' }));

      try {
        const bytes = await renderBytes(config);
        const blob = new Blob([new Uint8Array(bytes)], {
          type: 'application/pdf',
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');

        anchor.href = url;
        anchor.download = downloadFileName(config);
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        // Give the browser a moment to start the download before revoking.
        window.setTimeout(() => URL.revokeObjectURL(url), 10_000);

        appEvents.emit({
          type: 'selbstauskunft.pdfDownloaded',
          payload: configEventPayload(config),
        });
      } finally {
        setState((s) => ({ ...s, busy: null }));
      }
    },
    [appEvents, renderBytes],
  );

  const handleSaveToWorkspace = useCallback(
    async (config: SelfDisclosureConfigAuthor) => {
      const loggedIn = Boolean(user);

      appEvents.emit({
        type: 'selbstauskunft.workspaceSaveClicked',
        payload: { ...configEventPayload(config), logged_in: loggedIn },
      });

      // Logged in → load the save targets BEFORE the dialog decision (M1,
      // Sanierungs-Spec §3): 0 team workspaces takes the SAME park path as
      // anonymous users – the claim page orchestrates (sees 0 workspaces →
      // `/onboarding?template_token=`) instead of a dead-end dialog. The
      // picker state carries config + targets – no non-null assertion at
      // confirm time (N3), no load phase in the dialog.
      if (loggedIn) {
        setState((s) => ({ ...s, saveError: null, busy: 'save' }));

        try {
          const { targets } = await loadSaveTargetsAction({});

          if (targets.length === 0) {
            const parked = await parkToolTemplateAction({ config });

            if (!parked.success || !parked.token) {
              setState((s) => ({
                ...s,
                saveError:
                  parked.error ??
                  'Speichern fehlgeschlagen. Bitte versuche es erneut.',
              }));
              return;
            }

            router.push(
              `/tools/selbstauskunft/claim?token=${encodeURIComponent(
                parked.token,
              )}`,
            );
            return;
          }

          setState((s) => ({
            ...s,
            picker: { open: true, config, targets },
          }));
        } catch {
          setState((s) => ({
            ...s,
            saveError: 'Speichern fehlgeschlagen. Bitte versuche es erneut.',
          }));
        } finally {
          setState((s) => ({ ...s, busy: null }));
        }

        return;
      }

      // Anonymous → park the config server-side (survives the auth boundary /
      // device switch, Spec §5.3), then continue into sign-up with `next` pointing
      // at the claim page. `getCallbackUrl`/`getSafeRedirectPath` on the auth side
      // validate the app-internal `next`.
      setState((s) => ({ ...s, saveError: null, busy: 'save' }));

      try {
        const parked = await parkToolTemplateAction({ config });

        if (!parked.success || !parked.token) {
          setState((s) => ({
            ...s,
            saveError:
              parked.error ??
              'Speichern fehlgeschlagen. Bitte versuche es erneut.',
          }));
          return;
        }

        const claimPath = `/tools/selbstauskunft/claim?token=${encodeURIComponent(
          parked.token,
        )}`;

        // authHref already carries `?lang=de` (marketing→app i18n seam), so the
        // claim `next` is appended with `&`.
        router.push(
          `${authHref('signUp', 'de')}&next=${encodeURIComponent(claimPath)}`,
        );
      } finally {
        setState((s) => ({ ...s, busy: null }));
      }
    },
    [appEvents, router, user],
  );

  const setPickerOpen = useCallback((open: boolean) => {
    if (!open) {
      setState((s) => ({ ...s, picker: { open: false } }));
    }
  }, []);

  const { busy, saveError, picker } = state;

  return (
    <div className="selbstauskunft-tool" data-test="selbstauskunft-generator">
      <GeneratorForm
        onDirty={handleDirty}
        // 🔴 Die Website setzt ihr Aussehen SELBST. Bis zum 19.08.2026 tat das
        // `marketing-extra.css` ueber die `data-test`-Attribute der Knoepfe —
        // ein Test-Attribut umzubenennen haette damit still das Layout dieser
        // Seite zerlegt, und niemand haette es beim Umbenennen gesehen.
        //
        // Die Klassen kommen aus dem Website-DS (`.btn`, `.btn-ghost`,
        // `.btn-secondary`), also aus derselben Sprache wie die Knoepfe im
        // Fussbereich darunter.
        schrittKnopfKlassen={{
          zurueck: 'btn btn-ghost selbstauskunft-step-back',
          weiter: 'btn btn-secondary selbstauskunft-step-next',
        }}
        renderFooter={(formState) => (
          // Aktionsgruppe rechts (GeneratorForm setzt „Zurück" links in dieselbe
          // Zeile). Reihenfolge nach Design-System (weniger → wichtiger):
          // PDF-Download (outline) · Speichern (PRIMARY, wichtigster CTA =
          // PLG-Funnel). Die Microcopy gehört zum Speichern-Button und steht
          // rechtsbündig unter der Gruppe.
          <div className="selbstauskunft-footer">
            <div className="selbstauskunft-actions">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy !== null}
                onClick={() => {
                  if (formState.config) void handleDownload(formState.config);
                  else formState.revealErrors();
                }}
                data-test="selbstauskunft-download-pdf"
              >
                {busy === 'download'
                  ? 'PDF wird erstellt…'
                  : 'PDF kostenlos herunterladen'}
              </button>

              <button
                type="button"
                className="btn btn-primary"
                disabled={busy !== null}
                onClick={() => {
                  if (formState.config) {
                    void handleSaveToWorkspace(formState.config);
                  } else formState.revealErrors();
                }}
                data-test="selbstauskunft-save-workspace"
              >
                {busy === 'save'
                  ? 'Wird gespeichert…'
                  : 'Vorlage in Locari speichern'}
              </button>
            </div>

            <p
              className="selbstauskunft-save-microcopy"
              data-test="selbstauskunft-save-microcopy"
            >
              Kostenloses Konto – deine Vorlage wartet dort auf dich.
            </p>
          </div>
        )}
      />

      {saveError ? (
        <p
          className="selbstauskunft-hint"
          data-test="selbstauskunft-save-error"
          role="alert"
        >
          {saveError}
        </p>
      ) : null}

      {picker.open ? (
        <WorkspaceSaveDialog
          open={picker.open}
          onOpenChange={setPickerOpen}
          targets={picker.targets}
          onConfirm={(accountIds) =>
            saveToolTemplateToWorkspacesAction({
              config: picker.config,
              accountIds,
            })
          }
        />
      ) : null}
    </div>
  );
}
